/**
 * Questo slice Redux gestisce lo stato relativo alle flashcard e alle cartelle.
 * - Definisce thunk asincroni per le chiamate API (recupero, creazione, eliminazione).
 * - Gestisce lo stato di caricamento e degli errori.
 * - Include azioni sincrone per pulire parti dello stato.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchClient } from '../api/fetchClient';

// --- Thunk Asincroni ---

/**
 * Thunk: fetchFoldersWithCards
 * - Esegue una chiamata API per recuperare tutte le cartelle dell'utente con le relative flashcard.
 * - In caso di successo, i dati vengono salvati nello stato.
 * - In caso di errore, il messaggio di errore viene salvato nello stato.
 */
export const fetchFoldersWithCards = createAsyncThunk(
  'flashcards/fetchFoldersWithCards',
  async (_, { rejectWithValue }) => {
    try {
      const data = await fetchClient('flashcards/list_user_details.php');
      return data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Thunk: generateFlashcards
 * - Invia un file al backend per la generazione di nuove flashcard.
 * - Utilizza 'fetch' standard perché invia dati di tipo 'FormData'.
 * - @param {FormData} formData - Dati del form contenenti il file e le opzioni.
 */
export const generateFlashcards = createAsyncThunk(
  'flashcards/generate',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await fetch('/api/flashcards/upload.php', {
        method: 'POST',
        body: formData,
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Errore durante la generazione');
      }
      return await response.json();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Thunk: deleteFlashcard
 * - Esegue una chiamata API per eliminare una singola flashcard.
 * - @param {number} cardId - L'ID della flashcard da eliminare.
 */
export const deleteFlashcard = createAsyncThunk(
  'flashcards/deleteFlashcard',
  async (cardId, { rejectWithValue }) => {
    try {
      await fetchClient('flashcards/delete_card.php', { method: 'POST', body: { card_id: cardId } });
      return cardId; // Ritorna l'ID per aggiornare lo stato nel reducer.
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Thunk: deleteFolder
 * - Esegue una chiamata API per eliminare un'intera cartella.
 * - @param {number} folderId - L'ID della cartella da eliminare.
 */
export const deleteFolder = createAsyncThunk(
  'flashcards/deleteFolder',
  async (folderId, { rejectWithValue }) => {
    try {
      await fetchClient('flashcards/delete_folder.php', { method: 'POST', body: { folder_id: folderId } });
      return folderId; // Ritorna l'ID per aggiornare lo stato.
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Thunk: toggleFolderVisibility
 * - Esegue una chiamata API per cambiare la visibilità di una cartella (pubblica/privata).
 * - @param {number} folderId - L'ID della cartella da modificare.
 */
export const toggleFolderVisibility = createAsyncThunk(
  'flashcards/toggleVisibility',
  async (folderId, { rejectWithValue }) => {
    try {
      const result = await fetchClient('flashcards/toggle_folder_visibility.php', { method: 'POST', body: { folder_id: folderId } });
      return { folderId, newStatus: result.newStatus }; // Ritorna l'ID e il nuovo stato.
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Thunk: fetchPublicFolders
 * - Esegue una chiamata API per recuperare l'elenco di tutte le cartelle pubbliche.
 */
export const fetchPublicFolders = createAsyncThunk(
  'flashcards/fetchPublicFolders',
  async (_, { rejectWithValue }) => {
    try {
      const data = await fetchClient('flashcards/list_public.php');
      return data.folders;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Thunk: fetchPublicFolderDetails
 * - Esegue una chiamata API per recuperare i dettagli di una specifica cartella pubblica.
 * - @param {number} folderId - L'ID della cartella pubblica.
 */
export const fetchPublicFolderDetails = createAsyncThunk(
  'flashcards/fetchPublicFolderDetails',
  async (folderId, { rejectWithValue }) => {
    try {
      const data = await fetchClient(`flashcards/get_public_folder.php?id=${folderId}`);
      return data.folder;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Thunk: updateFolder
 * - Esegue una chiamata API per aggiornare il nome di una cartella.
 * - @param {object} payload - Oggetto contenente 'folderId' e 'newName'.
 */
export const updateFolder = createAsyncThunk(
  'flashcards/updateFolder',
  async ({ folderId, newName }, { rejectWithValue }) => {
    try {
      await fetchClient('flashcards/update_folder.php', { method: 'POST', body: { folder_id: folderId, new_name: newName } });
      return { folderId, newName }; // Ritorna i dati per l'aggiornamento dello stato.
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);


// --- Stato Iniziale dello Slice ---
const initialState = {
  detailedFolders: [],         // Contiene le cartelle dell'utente con le flashcard.
  publicFolders: [],           // Contiene l'elenco delle cartelle pubbliche.
  currentPublicFolder: null,   // Contiene i dettagli della cartella pubblica selezionata.
  generatedCards: null,        // Contiene le card appena generate per l'anteprima.
  loading: false,              // Flag di caricamento per le operazioni asincrone.
  error: null,                 // Contiene eventuali messaggi di errore.
};

// --- Creazione dello Slice ---
const flashcardSlice = createSlice({
  name: 'flashcards',
  initialState,
  // Azioni sincrone definite qui.
  reducers: {
    // Resetta lo stato delle card generate.
    clearGeneratedCards: (state) => {
      state.generatedCards = null;
    },
    // Resetta lo stato della cartella pubblica corrente.
    clearCurrentPublicFolder: (state) => {
      state.currentPublicFolder = null;
    }
  },
  // Gestisce le azioni definite dai thunk asincroni.
  extraReducers: (builder) => {
    builder
      // --- Casi per generateFlashcards ---
      .addCase(generateFlashcards.pending, (state) => {
        state.loading = true; state.error = null; state.generatedCards = null;
      })
      .addCase(generateFlashcards.fulfilled, (state, action) => {
        state.loading = false;
        state.generatedCards = action.payload.cards;
      })
      .addCase(generateFlashcards.rejected, (state, action) => {
        state.loading = false; state.error = action.payload;
      })

      // --- Casi per fetchFoldersWithCards ---
      .addCase(fetchFoldersWithCards.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(fetchFoldersWithCards.fulfilled, (state, action) => {
        state.loading = false;
        state.detailedFolders = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchFoldersWithCards.rejected, (state, action) => {
        state.loading = false; state.error = action.payload;
      })

      // --- Casi per l'eliminazione ---
      .addCase(deleteFlashcard.fulfilled, (state, action) => {
        const deletedCardId = action.payload;
        // Rimuove la flashcard eliminata dallo stato in modo immutabile.
        state.detailedFolders = state.detailedFolders.map(folder => ({
          ...folder,
          flashcards: folder.flashcards.filter(card => card.id !== deletedCardId)
        }));
      })
      .addCase(deleteFolder.fulfilled, (state, action) => {
        const deletedFolderId = action.payload;
        // Rimuove la cartella eliminata dallo stato.
        state.detailedFolders = state.detailedFolders.filter(folder => folder.id !== deletedFolderId);
      })

      // --- Casi per l'aggiornamento (visibilità, nome) ---
      .addCase(toggleFolderVisibility.fulfilled, (state, action) => {
        const { folderId, newStatus } = action.payload;
        const folder = state.detailedFolders.find(f => f.id === folderId);
        if (folder) folder.pubblica = newStatus;
      })
      .addCase(updateFolder.fulfilled, (state, action) => {
        const { folderId, newName } = action.payload;
        const folder = state.detailedFolders.find(f => f.id === folderId);
        if (folder) folder.nome = newName;
      })

      // --- Casi per le cartelle pubbliche ---
      .addCase(fetchPublicFolders.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(fetchPublicFolders.fulfilled, (state, action) => {
        state.loading = false;
        state.publicFolders = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchPublicFolders.rejected, (state, action) => {
        state.loading = false; state.error = action.payload;
      })
      .addCase(fetchPublicFolderDetails.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(fetchPublicFolderDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.currentPublicFolder = action.payload;
      })
      .addCase(fetchPublicFolderDetails.rejected, (state, action) => {
        state.loading = false; state.error = action.payload;
      });
  },
});

// Esporta le azioni sincrone e il reducer.
export const { clearGeneratedCards, clearCurrentPublicFolder } = flashcardSlice.actions;
export default flashcardSlice.reducer;